import type { CSSProperties, MouseEvent, ReactElement, ReactNode, WheelEvent } from "react";
import { Cbutton } from "@/components/ui/Cbutton";
import { ButtonPreset } from "@/constants/componentPresets";
import { getFontFamily } from "@/theme/typography";
import { GLASS_SHADOW, MINT } from "./chatteaser3.constants";
import { MOBILE_TABBAR_STYLE } from "./chatteaser3.mobile.styles";
// Horizontal bottom-tab designs (compared via the mobile tweaks panel). They
// differ in how the tabs are designed, spread, and how the active is picked.
// Carousel / Pills / Underline are label-rail designs; Dots / Beads / Track /
// Glow are the compact "dot" family.
export const MOBILE_TAB_VARIANT = {
    Reel: "Reel",
    Reel2: "Reel2",
    Reel3: "Reel3",
    Carousel: "Carousel",
    Pills: "Pills",
    Underline: "Underline",
    Dots: "Dots",
    Beads: "Beads",
    Track: "Track",
    Glow: "Glow",
    Hybrid1: "Hybrid1",
    Hybrid2: "Hybrid2",
    Hybrid3: "Hybrid3",
    Hybrid4: "Hybrid4",
    Hybrid5: "Hybrid5",
    Hybrid6: "Hybrid6",
    Hybrid7: "Hybrid7",
    Hybrid8: "Hybrid8",
    Hybrid9: "Hybrid9",
    Hybrid10: "Hybrid10",
} as const;
type MobileTabVariantLocal = (typeof MOBILE_TAB_VARIANT)[keyof typeof MOBILE_TAB_VARIANT];
export const DEFAULT_MOBILE_TAB: MobileTabVariantLocal = MOBILE_TAB_VARIANT.Reel;
const MOBILE_TAB_HYBRID_PREFIX = "Hybrid";
const MOBILE_TAB_REEL_PREFIX = "Reel";
enum MobileTabArrowSide {
    Left = "left",
    Right = "right"
}
export const MOBILE_TAB_VARIANT_ORDER: readonly MobileTabVariantLocal[] = [
    MOBILE_TAB_VARIANT.Reel,
    MOBILE_TAB_VARIANT.Reel2,
    MOBILE_TAB_VARIANT.Reel3,
    MOBILE_TAB_VARIANT.Carousel,
    MOBILE_TAB_VARIANT.Pills,
    MOBILE_TAB_VARIANT.Underline,
    MOBILE_TAB_VARIANT.Dots,
    MOBILE_TAB_VARIANT.Beads,
    MOBILE_TAB_VARIANT.Track,
    MOBILE_TAB_VARIANT.Glow,
    MOBILE_TAB_VARIANT.Hybrid1,
    MOBILE_TAB_VARIANT.Hybrid2,
    MOBILE_TAB_VARIANT.Hybrid3,
    MOBILE_TAB_VARIANT.Hybrid4,
    MOBILE_TAB_VARIANT.Hybrid5,
    MOBILE_TAB_VARIANT.Hybrid6,
    MOBILE_TAB_VARIANT.Hybrid7,
    MOBILE_TAB_VARIANT.Hybrid8,
    MOBILE_TAB_VARIANT.Hybrid9,
    MOBILE_TAB_VARIANT.Hybrid10,
];
interface TabsProps {
    tabs: readonly string[];
    activeTab: number;
    onSelect: (index: number) => void;
    spread: number;
    scrollProgress: number; // Signed -1..1
    onPrev: () => void;
    onNext: () => void;
    atStart: boolean;
    atEnd: boolean;
}
const GLASS_PILL_SHADOW = "inset 0px 1px 1px 0px #ffffff3b, 1px 2px 2px -1px #00000045";
const SLIDE_TRANSITION = "transform 0.36s cubic-bezier(0.22,1,0.36,1), opacity 0.3s ease, color 0.25s ease, background 0.25s ease, border-color 0.25s ease";
const REEL_WRAP_PAD_X = 16;
const REEL_BAR_MAX_WIDTH = 480;
const REEL_BAR_EDGE_RESERVE = 130;
const BARE_BUTTON: CSSProperties = {
    backgroundColor: "transparent",
    borderColor: "transparent",
    borderStyle: "none",
    borderWidth: 0,
    fontFamily: getFontFamily(),
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
};
interface MobileTabButtonProps {
    ariaLabel?: string;
    children?: ReactNode;
    disabled?: boolean;
    onClick: (event?: MouseEvent<HTMLDivElement>) => void;
    style: CSSProperties;
}
function MobileTabButton({ ariaLabel, children, disabled = false, onClick, style, }: MobileTabButtonProps): ReactElement {
    return (<Cbutton rootAttributes={{ "data-el": "Cchatteaser3/MobileTabButton/Trigger" }} preset={ButtonPreset.Ghost} ariaLabel={ariaLabel} disabled={disabled} onClick={onClick} padding={0} radius={999} style={style} containerStyle={{
            position: "relative",
            width: "100%",
            height: "100%",
            minWidth: 0,
            color: "inherit",
            fontFamily: "inherit",
        }}>
      {children}
    </Cbutton>);
}
// Each tab is absolutely centered; the active sits at the centre, neighbours
// offset by `distance * spread`. Tapping re-centres (CSS transition).
function centeredSlot(distance: number, spread: number, scale = 1): CSSProperties {
    return {
        position: "absolute",
        left: "50%",
        top: "50%",
        transform: `translate(calc(-50% + ${distance * spread}px), -50%) scale(${scale})`,
        transition: SLIDE_TRANSITION,
        whiteSpace: "nowrap",
    };
}
// ---------------------------------------------------------------------------
// Label-rail designs
// ---------------------------------------------------------------------------
function MobileTabsCarousel({ tabs, activeTab, onSelect, spread, scrollProgress, }: TabsProps): ReactElement {
    return (
      <>
        {tabs.map((tab, index) => {
            const distance = index - activeTab - scrollProgress;
            const distanceAbs = Math.abs(distance);
            const isActive = distanceAbs < 0.5;
            const scale = Math.max(0.68, 1 - distanceAbs * 0.16);
            return (<MobileTabButton key={tab} ariaLabel={tab} onClick={() => onSelect(index)} style={{
                    ...BARE_BUTTON,
                    ...centeredSlot(distance, spread, scale),
                    fontSize: 19,
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? "#ffffff" : "rgba(255,255,255,0.55)",
                    opacity: Math.max(0.22, 1 - distanceAbs * 0.42),
                    transition: scrollProgress !== 0 ? "none" : SLIDE_TRANSITION,
                }}>
              {tab}
            </MobileTabButton>);
        })}
      </>
    );
}
// Reel — the show4 bench's bottom nav, ported in before that bench was removed:
// the active label sits dead-centre,
// shown in FULL (never truncated), while neighbours slide out and fade at the
// bar's masked edges. The bar grows to fill the width so items get breathing
// room. The centre-item + arrow treatments come in switchable flavours
// (Reel = Glass, Reel2 = Glow, Reel3 = Chip).
const REEL_ROW_STYLE: CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 14,
    width: "100%",
    boxSizing: "border-box",
    paddingInline: REEL_WRAP_PAD_X,
};
const REEL_BAR_MASK = "linear-gradient(to right, transparent 0, #000 13%, #000 87%, transparent 100%)";
// Wide enough to show the longest category label in full, but sized (not full
// width) so the flanking arrows hug it instead of sitting at the screen edges.
const REEL_BAR_BASE: CSSProperties = {
    position: "relative",
    width: `min(${REEL_BAR_MAX_WIDTH}px, calc(100vw - ${REEL_BAR_EDGE_RESERVE + REEL_WRAP_PAD_X * 2}px))`,
    flexShrink: 0,
    height: 54,
    overflow: "hidden",
    maskImage: REEL_BAR_MASK,
    WebkitMaskImage: REEL_BAR_MASK,
};
interface ReelDesign {
    bar: CSSProperties;
    itemSpreadCap: number;
    activeLabel: CSSProperties;
    inactiveLabel: CSSProperties;
    arrow: (disabled: boolean) => CSSProperties;
    prevGlyph: string;
    nextGlyph: string;
}
// Flavour 1 — Glass: frosted pill, bold mint centre, mint glass circle arrows.
const REEL_DESIGN_GLASS: ReelDesign = {
    bar: {
        ...REEL_BAR_BASE,
        borderRadius: 90,
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.06)",
        boxShadow: GLASS_SHADOW,
    },
    itemSpreadCap: 196,
    activeLabel: { fontSize: 18, fontWeight: 700, letterSpacing: "0.02em", color: MINT },
    inactiveLabel: { fontSize: 15, fontWeight: 500, letterSpacing: "0.01em", color: "rgba(255,255,255,0.5)" },
    arrow: (disabled) => ({
        ...BARE_BUTTON,
        width: 46,
        height: 46,
        flexShrink: 0,
        borderRadius: "50%",
        backgroundColor: "rgba(95,243,184,0.06)",
        borderColor: "rgba(95,243,184,0.22)",
        borderStyle: "solid",
        borderWidth: 1,
        boxShadow: GLASS_SHADOW,
        color: disabled ? "rgba(95,243,184,0.25)" : MINT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 26,
        lineHeight: 1,
        paddingBottom: 3,
        opacity: disabled ? 0.55 : 1,
        cursor: disabled ? "default" : "pointer",
    }),
    prevGlyph: "‹",
    nextGlyph: "›",
};
// Flavour 2 — Glow: no pill; centre label glows with a mint underline; the
// arrows are bare, glowing chevrons.
const REEL_DESIGN_GLOW: ReelDesign = {
    bar: { ...REEL_BAR_BASE, background: "transparent" },
    itemSpreadCap: 202,
    activeLabel: {
        fontSize: 18,
        fontWeight: 700,
        letterSpacing: "0.03em",
        color: MINT,
        textShadow: "0 0 14px rgba(95,243,184,0.55)",
        // Underline via text-decoration LONGHANDS — NOT borderBottom / paddingBottom.
        // BARE_BUTTON sets the `border` AND `padding` shorthands, so toggling a
        // matching longhand on tab change makes React warn ("Removing a style
        // property during rerender … don't mix shorthand and non-shorthand") and can
        // glitch styles. text-decoration-* has no shorthand here, so it never warns.
        textDecorationLine: "underline",
        textDecorationColor: MINT,
        textDecorationThickness: "2px",
        textUnderlineOffset: "5px",
    },
    inactiveLabel: { fontSize: 15, fontWeight: 500, letterSpacing: "0.01em", color: "rgba(255,255,255,0.42)" },
    arrow: (disabled) => ({
        ...BARE_BUTTON,
        width: 40,
        height: 40,
        flexShrink: 0,
        color: disabled ? "rgba(95,243,184,0.2)" : MINT,
        textShadow: disabled ? "none" : "0 0 12px rgba(95,243,184,0.6)",
        fontSize: 30,
        lineHeight: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        paddingBottom: 4,
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? "default" : "pointer",
    }),
    prevGlyph: "‹",
    nextGlyph: "›",
};
// Flavour 3 — Chip: dark glass track; centre label wrapped in a mint chip;
// squared glass arrows with guillemets.
const REEL_DESIGN_CHIP: ReelDesign = {
    bar: {
        ...REEL_BAR_BASE,
        borderRadius: 16,
        background: "rgba(0,0,0,0.32)",
        boxShadow: GLASS_PILL_SHADOW,
    },
    itemSpreadCap: 202,
    activeLabel: {
        fontSize: 16,
        fontWeight: 600,
        letterSpacing: "0.02em",
        color: MINT,
        padding: "7px 18px",
        borderRadius: 999,
        background: "rgba(95,243,184,0.13)",
        border: "1px solid rgba(95,243,184,0.4)",
    },
    inactiveLabel: { fontSize: 14, fontWeight: 500, letterSpacing: "0.01em", color: "rgba(255,255,255,0.45)" },
    arrow: (disabled) => ({
        ...BARE_BUTTON,
        width: 44,
        height: 44,
        flexShrink: 0,
        borderRadius: 13,
        backgroundColor: "rgba(255,255,255,0.05)",
        borderColor: "rgba(255,255,255,0.1)",
        borderStyle: "solid",
        borderWidth: 1,
        boxShadow: GLASS_PILL_SHADOW,
        color: disabled ? "rgba(255,255,255,0.2)" : MINT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 22,
        lineHeight: 1,
        paddingBottom: 2,
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? "default" : "pointer",
    }),
    prevGlyph: "«",
    nextGlyph: "»",
};
function ReelBar({ design, tabs, activeTab, onSelect, spread, scrollProgress, onPrev, onNext, atStart, atEnd, }: TabsProps & {
    design: ReelDesign;
}): ReactElement {
    const itemSpread = Math.min(spread, design.itemSpreadCap);
    return (<div data-el="Cchatteaser3/ReelBar/Root" style={REEL_ROW_STYLE}>
      <MobileTabButton ariaLabel="Previous category" disabled={atStart} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onPrev();
        }} style={design.arrow(atStart)}>
        {design.prevGlyph}
      </MobileTabButton>
      <div data-el="Cchatteaser3/ReelBar/Rail" style={design.bar}>
        {tabs.map((tab, index) => {
            const distance = index - activeTab - scrollProgress;
            const distanceAbs = Math.abs(distance);
            const isActive = distanceAbs < 0.5;
            const scale = isActive ? 1 : Math.max(0.8, 1 - distanceAbs * 0.1);
            return (<MobileTabButton key={tab} ariaLabel={tab} onClick={(e) => {
                    e?.preventDefault();
                    e?.stopPropagation();
                    onSelect(index);
                }} style={{
                    ...BARE_BUTTON,
                    ...centeredSlot(distance, itemSpread, scale),
                    ...(isActive ? design.activeLabel : design.inactiveLabel),
                    opacity: isActive ? 1 : Math.max(0.1, 1 - distanceAbs * 0.5),
                    transition: scrollProgress !== 0 ? "none" : SLIDE_TRANSITION,
                }}>
              {tab}
            </MobileTabButton>);
        })}
      </div>
      <MobileTabButton ariaLabel="Next category" disabled={atEnd} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onNext();
        }} style={design.arrow(atEnd)}>
        {design.nextGlyph}
      </MobileTabButton>
    </div>);
}
function MobileTabsReel(props: TabsProps): ReactElement {
    return (
      <ReelBar {...props} design={REEL_DESIGN_GLASS}/>
    );
}
function MobileTabsReel2(props: TabsProps): ReactElement {
    return (
      <ReelBar {...props} design={REEL_DESIGN_GLOW}/>
    );
}
function MobileTabsReel3(props: TabsProps): ReactElement {
    return (
      <ReelBar {...props} design={REEL_DESIGN_CHIP}/>
    );
}
function MobileTabsPills({ tabs, activeTab, onSelect, spread, scrollProgress, }: TabsProps): ReactElement {
    return (
      <>
        {tabs.map((tab, index) => {
            const distance = index - activeTab - scrollProgress;
            const distanceAbs = Math.abs(distance);
            const isActive = distanceAbs < 0.5;
            return (<MobileTabButton key={tab} ariaLabel={tab} onClick={() => onSelect(index)} style={{
                    ...BARE_BUTTON,
                    ...centeredSlot(distance, spread),
                    fontSize: 16,
                    paddingBottom: 9,
                    paddingLeft: 18,
                    paddingRight: 18,
                    paddingTop: 9,
                    borderRadius: 90,
                    fontWeight: isActive ? 600 : 400,
                    backgroundColor: isActive
                        ? "rgba(95,243,184,0.14)"
                        : "rgba(255,255,255,0.04)",
                    borderColor: isActive
                        ? "rgba(95,243,184,0.5)"
                        : "rgba(255,255,255,0.09)",
                    borderStyle: "solid",
                    borderWidth: 1,
                    color: isActive ? MINT : "rgba(255,255,255,0.6)",
                    opacity: Math.max(0.4, 1 - distanceAbs * 0.3),
                    transition: scrollProgress !== 0 ? "none" : SLIDE_TRANSITION,
                }}>
              {tab}
            </MobileTabButton>);
        })}
      </>
    );
}
function MobileTabsUnderline({ tabs, activeTab, onSelect, spread, scrollProgress, }: TabsProps): ReactElement {
    return (
      <>
        {tabs.map((tab, index) => {
            const distance = index - activeTab - scrollProgress;
            const distanceAbs = Math.abs(distance);
            const isActive = distanceAbs < 0.5;
            return (<MobileTabButton key={tab} ariaLabel={tab} onClick={() => onSelect(index)} style={{
                    ...BARE_BUTTON,
                    ...centeredSlot(distance, spread),
                    fontSize: 18,
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? MINT : "rgba(255,255,255,0.5)",
                    opacity: Math.max(0.3, 1 - distanceAbs * 0.36),
                    transition: scrollProgress !== 0 ? "none" : SLIDE_TRANSITION,
                }}>
              {tab}
            </MobileTabButton>);
        })}
        <div style={{
            position: "absolute",
            left: "50%",
            bottom: 20,
            width: 46,
            height: 3,
            borderRadius: 2,
            backgroundColor: MINT,
            transform: "translateX(-50%)",
        }}/>
      </>
    );
}
// ---------------------------------------------------------------------------
// Dot family
// ---------------------------------------------------------------------------
function MobileTabsDots({ tabs, activeTab, onSelect, spread, scrollProgress, }: TabsProps): ReactElement {
    const gap = Math.max(9, spread / 14);
    return (
      <div style={DOTS_WRAP_STYLE}>
        <span style={DOTS_LABEL_STYLE}>
          {tabs[activeTab]}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap }}>
          {tabs.map((tab, index) => {
            const isActive = index === activeTab;
            return (<MobileTabButton key={tab} ariaLabel={tab} onClick={() => onSelect(index)} style={{
                    ...BARE_BUTTON,
                    width: isActive ? 24 : 8,
                    height: 8,
                    borderRadius: 4,
                    transition: "width 0.3s ease, background 0.3s ease",
                    backgroundColor: isActive ? MINT : "rgba(255,255,255,0.25)",
                }}/>);
        })}
        </div>
      </div>
    );
}
// Beads — the dot row sits inside a dark glass capsule (matches the @mention /
// avatar-logo pills), active bead stretches to a mint lozenge.
function MobileTabsBeads({ tabs, activeTab, onSelect, spread, scrollProgress, }: TabsProps): ReactElement {
    const gap = Math.max(8, spread / 18);
    return (
      <div style={DOTS_WRAP_STYLE}>
        <span style={DOTS_LABEL_STYLE}>
          {tabs[activeTab]}
        </span>
        <div style={{ ...BEADS_TRACK_STYLE, gap }}>
          {tabs.map((tab, index) => {
            const isActive = index === activeTab;
            return (<MobileTabButton key={tab} ariaLabel={tab} onClick={() => onSelect(index)} style={{
                    ...BARE_BUTTON,
                    width: isActive ? 22 : 8,
                    height: 8,
                    borderRadius: 4,
                    transition: "width 0.32s cubic-bezier(0.22,1,0.36,1), background 0.3s ease",
                    backgroundColor: isActive ? MINT : "rgba(255,255,255,0.3)",
                }}/>);
        })}
        </div>
      </div>
    );
}
// Track — a segmented glass rail with a mint thumb that slides to the active
// segment (reads like a progress / slider control).
function MobileTabsTrack({ tabs, activeTab, onSelect, spread, scrollProgress, }: TabsProps): ReactElement {
    const count = tabs.length;
    const railWidth = Math.min(300, Math.max(170, spread));
    return (
      <div style={DOTS_WRAP_STYLE}>
        <span style={DOTS_LABEL_STYLE}>
          {tabs[activeTab]}
        </span>
        <div style={{ ...TRACK_RAIL_STYLE, width: railWidth }}>
          <div style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            width: `${100 / count}%`,
            left: `${(activeTab / count) * 100}%`,
            borderRadius: 90,
            backgroundColor: MINT,
            transition: "left 0.36s cubic-bezier(0.22,1,0.36,1)",
        }}/>

          <div style={TRACK_SEGMENTS_STYLE}>
            {tabs.map((tab, index) => (<MobileTabButton key={tab} ariaLabel={tab} onClick={() => onSelect(index)} style={{ ...BARE_BUTTON, flex: 1, height: "100%" }}/>))}
          </div>
        </div>
      </div>
    );
}
// Glow — minimal round dots, the active one lifts with a mint glow halo.
function MobileTabsGlow({ tabs, activeTab, onSelect, spread, scrollProgress, }: TabsProps): ReactElement {
    const gap = Math.max(13, spread / 12);
    return (
      <div style={DOTS_WRAP_STYLE}>
        <span style={DOTS_LABEL_STYLE}>
          {tabs[activeTab]}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap }}>
          {tabs.map((tab, index) => {
            const isActive = index === activeTab;
            return (<MobileTabButton key={tab} ariaLabel={tab} onClick={() => onSelect(index)} style={{
                    ...BARE_BUTTON,
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: isActive ? MINT : "rgba(255,255,255,0.22)",
                    boxShadow: isActive
                        ? "0 0 10px 2px rgba(95,243,184,0.65)"
                        : "none",
                    transform: isActive ? "scale(1.2)" : "scale(1)",
                    transition: "background 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease",
                }}/>);
        })}
        </div>
      </div>
    );
}
const DOTS_WRAP_STYLE: CSSProperties = {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
};
const DOTS_LABEL_STYLE: CSSProperties = {
    fontSize: 18,
    fontWeight: 600,
    color: MINT,
    fontFamily: getFontFamily(),
    whiteSpace: "nowrap",
};
const BEADS_TRACK_STYLE: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    padding: "9px 16px",
    borderRadius: 90,
    backgroundColor: "rgba(0,0,0,0.4)",
    boxShadow: GLASS_PILL_SHADOW,
};
const TRACK_RAIL_STYLE: CSSProperties = {
    position: "relative",
    height: 12,
    borderRadius: 90,
    backgroundColor: "rgba(0,0,0,0.45)",
    boxShadow: GLASS_PILL_SHADOW,
    overflow: "hidden",
};
const TRACK_SEGMENTS_STYLE: CSSProperties = {
    position: "absolute",
    inset: 0,
    display: "flex",
};
// ---------------------------------------------------------------------------
// Hybrids
// ---------------------------------------------------------------------------
function MobileTabsHybrid1({ tabs, activeTab, onSelect, spread, scrollProgress, onPrev, onNext, atStart, atEnd, }: TabsProps): ReactElement {
    // A scrolling text carousel on top, with a scrolling dots track below it.
    return (
      <div style={{ ...DOTS_WRAP_STYLE, gap: 16 }}>
        {/* Text Carousel Layer */}
        <div style={{
            position: "relative",
            width: "100%",
            height: 32,
            overflow: "hidden",
        }}>
          {tabs.map((tab, index) => {
            const distance = index - activeTab - scrollProgress;
            const distanceAbs = Math.abs(distance);
            const isActive = distanceAbs < 0.5;
            const scale = Math.max(0.75, 1 - distanceAbs * 0.15);
            return (<MobileTabButton key={tab} ariaLabel={tab} onClick={(e) => {
                    e?.preventDefault();
                    e?.stopPropagation();
                    onSelect(index);
                }} style={{
                    ...BARE_BUTTON,
                    ...centeredSlot(distance, spread, scale),
                    color: isActive ? "#fff" : "rgba(255,255,255,0.4)",
                    fontSize: 20,
                    fontWeight: isActive ? 600 : 500,
                    opacity: Math.max(0, 1 - distanceAbs * 0.7),
                    transition: scrollProgress !== 0 ? "none" : SLIDE_TRANSITION,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: isActive ? 2 : 1,
                }}>
                {tab}
              </MobileTabButton>);
        })}
        </div>

        {/* Expanded Dots & Arrows Layer */}
        <div style={{
            position: "relative",
            width: "100%",
            maxWidth: 360,
            height: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 24px",
        }}>
          <MobileTabButton ariaLabel="Previous category" disabled={atStart} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onPrev();
        }} style={{
            ...BARE_BUTTON,
            color: atStart ? "rgba(255,255,255,0.15)" : MINT,
            fontSize: 36,
            lineHeight: 1,
            paddingBottom: 4,
        }}>
            ‹
          </MobileTabButton>

          <div style={{
            position: "relative",
            flex: 1,
            height: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
        }}>
            {tabs.map((tab, index) => {
            const distance = index - activeTab - scrollProgress;
            const distanceAbs = Math.abs(distance);
            const isActive = distanceAbs < 0.5;
            return (<MobileTabButton key={tab} ariaLabel={tab} onClick={(e) => {
                    e?.preventDefault();
                    e?.stopPropagation();
                    onSelect(index);
                }} style={{
                    ...BARE_BUTTON,
                    ...centeredSlot(distance, 26),
                    width: isActive ? 24 : 8,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: isActive ? MINT : "rgba(255,255,255,0.25)",
                    opacity: Math.max(0, 1 - distanceAbs * 0.35),
                    transition: scrollProgress !== 0 ? "none" : "all 0.3s ease",
                }}/>);
        })}
          </div>

          <MobileTabButton ariaLabel="Next category" disabled={atEnd} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onNext();
        }} style={{
            ...BARE_BUTTON,
            color: atEnd ? "rgba(255,255,255,0.15)" : MINT,
            fontSize: 36,
            lineHeight: 1,
            paddingBottom: 4,
        }}>
            ›
          </MobileTabButton>
        </div>
      </div>
    );
}
function MobileTabsHybrid2({ tabs, activeTab, onSelect, spread, scrollProgress, onPrev, onNext, atStart, atEnd, }: TabsProps): ReactElement {
    // A scrolling beads track (inside a glass capsule) with a stationary label above it.
    return (
      <div style={DOTS_WRAP_STYLE}>
        <span style={DOTS_LABEL_STYLE}>
          {tabs[Math.round(activeTab + scrollProgress)] || tabs[activeTab]}
        </span>
        <div style={{
            ...BEADS_TRACK_STYLE,
            position: "relative",
            width: 160,
            height: 26,
            padding: "0 8px",
            justifyContent: "space-between",
        }}>
          <MobileTabButton ariaLabel="Previous category" disabled={atStart} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onPrev();
        }} style={{
            ...BARE_BUTTON,
            color: atStart ? "rgba(255,255,255,0.15)" : "#fff",
            fontSize: 20,
            zIndex: 2,
            paddingBottom: 2,
        }}>
            ‹
          </MobileTabButton>
          <div style={{
            position: "absolute",
            left: 28,
            right: 28,
            top: 0,
            bottom: 0,
            overflow: "hidden",
        }}>
            {tabs.map((tab, index) => {
            const distance = index - activeTab - scrollProgress;
            const distanceAbs = Math.abs(distance);
            const isActive = distanceAbs < 0.5;
            return (<MobileTabButton key={tab} ariaLabel={tab} onClick={(e) => {
                    e?.preventDefault();
                    e?.stopPropagation();
                    onSelect(index);
                }} style={{
                    ...BARE_BUTTON,
                    ...centeredSlot(distance, Math.min(spread, 24)),
                    width: isActive ? 22 : 8,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: isActive ? MINT : "rgba(255,255,255,0.3)",
                    transition: scrollProgress !== 0 ? "none" : "all 0.3s ease",
                }}/>);
        })}
          </div>
          <MobileTabButton ariaLabel="Next category" disabled={atEnd} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onNext();
        }} style={{
            ...BARE_BUTTON,
            color: atEnd ? "rgba(255,255,255,0.15)" : "#fff",
            fontSize: 20,
            zIndex: 2,
            paddingBottom: 2,
        }}>
            ›
          </MobileTabButton>
        </div>
      </div>
    );
}
function MobileTabsHybrid3({ tabs, activeTab, onSelect, spread, scrollProgress, onPrev, onNext, atStart, atEnd, }: TabsProps): ReactElement {
    // Center item is text and a dot. Side items are just dots.
    return (
      <div style={{
            position: "relative",
            width: "100%",
            height: 40,
            display: "flex",
            justifyContent: "center",
        }}>
        <MobileTabButton ariaLabel="Previous category" disabled={atStart} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onPrev();
        }} style={{
            ...BARE_BUTTON,
            position: "absolute",
            left: "50%",
            marginLeft: -120,
            top: -2,
            color: atStart ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.5)",
            fontSize: 26,
        }}>
          ‹
        </MobileTabButton>
        {tabs.map((tab, index) => {
            const distance = index - activeTab - scrollProgress;
            const distanceAbs = Math.abs(distance);
            const isCenter = distanceAbs < 0.5;
            return (<MobileTabButton key={tab} ariaLabel={tab} onClick={(e) => {
                    e?.preventDefault();
                    e?.stopPropagation();
                    onSelect(index);
                }} style={{
                    ...BARE_BUTTON,
                    ...centeredSlot(distance, spread),
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    opacity: Math.max(0.1, 1 - distanceAbs * 0.4),
                    transition: scrollProgress !== 0 ? "none" : "all 0.3s ease",
                }}>
              {isCenter && (<span style={{
                        position: "absolute",
                        bottom: 16,
                        color: MINT,
                        fontSize: 18,
                        fontWeight: 600,
                    }}>
                  {tab}
                </span>)}
              <div style={{
                    width: isCenter ? 24 : 8,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: isCenter ? MINT : "rgba(255,255,255,0.25)",
                    transition: scrollProgress !== 0 ? "none" : "all 0.3s ease",
                }}/>
            </MobileTabButton>);
        })}
        <MobileTabButton ariaLabel="Next category" disabled={atEnd} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onNext();
        }} style={{
            ...BARE_BUTTON,
            position: "absolute",
            left: "50%",
            marginLeft: 120,
            top: -2,
            color: atEnd ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.5)",
            fontSize: 26,
        }}>
          ›
        </MobileTabButton>
      </div>
    );
}
function MobileTabsHybrid4({ tabs, activeTab, onSelect, spread, scrollProgress, onPrev, onNext, atStart, atEnd, }: TabsProps): ReactElement {
    // A sleek track pill behind everything. Items are text in center, dots on the sides.
    return (
      <>
        <MobileTabButton ariaLabel="Previous category" disabled={atStart} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onPrev();
        }} style={{
            ...BARE_BUTTON,
            position: "absolute",
            left: "50%",
            marginLeft: -105,
            top: "50%",
            transform: "translate(-50%, -50%)",
            color: atStart ? "rgba(255,255,255,0.15)" : MINT,
            fontSize: 24,
            paddingBottom: 2,
        }}>
          ‹
        </MobileTabButton>
        <div style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 140,
            height: 36,
            backgroundColor: "rgba(255,255,255,0.05)",
            borderRadius: 18,
            border: "1px solid rgba(255,255,255,0.08)",
            pointerEvents: "none",
        }}/>
        {tabs.map((tab, index) => {
            const distance = index - activeTab - scrollProgress;
            const distanceAbs = Math.abs(distance);
            const isCenter = distanceAbs < 0.5;
            return (<MobileTabButton key={tab} ariaLabel={tab} onClick={(e) => {
                    e?.preventDefault();
                    e?.stopPropagation();
                    onSelect(index);
                }} style={{
                    ...BARE_BUTTON,
                    ...centeredSlot(distance, spread),
                    color: isCenter ? MINT : "transparent",
                    fontSize: 16,
                    fontWeight: 600,
                    letterSpacing: "0.05em",
                    transition: scrollProgress !== 0 ? "none" : "all 0.3s ease",
                }}>
              {isCenter ? (tab) : (<div style={{
                        width: 6,
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: "rgba(255,255,255,0.3)",
                        margin: "0 auto",
                    }}/>)}
            </MobileTabButton>);
        })}
        <MobileTabButton ariaLabel="Next category" disabled={atEnd} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onNext();
        }} style={{
            ...BARE_BUTTON,
            position: "absolute",
            left: "50%",
            marginLeft: 105,
            top: "50%",
            transform: "translate(-50%, -50%)",
            color: atEnd ? "rgba(255,255,255,0.15)" : MINT,
            fontSize: 24,
            paddingBottom: 2,
        }}>
          ›
        </MobileTabButton>
      </>
    );
}
function MobileTabsHybrid5({ tabs, activeTab, onSelect, spread, scrollProgress, onPrev, onNext, atStart, atEnd, }: TabsProps): ReactElement {
    // Center is text. Inactive is `●` character that scales down.
    return (
      <>
        <MobileTabButton ariaLabel="Previous category" disabled={atStart} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onPrev();
        }} style={{
            ...BARE_BUTTON,
            position: "absolute",
            left: "50%",
            marginLeft: -120,
            top: "50%",
            transform: "translate(-50%, -50%)",
            color: atStart ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.4)",
            fontSize: 20,
        }}>
          ⟨
        </MobileTabButton>
        {tabs.map((tab, index) => {
            const distance = index - activeTab - scrollProgress;
            const distanceAbs = Math.abs(distance);
            const isCenter = distanceAbs < 0.5;
            const scale = isCenter ? 1 : 0.6;
            return (<MobileTabButton key={tab} ariaLabel={tab} onClick={(e) => {
                    e?.preventDefault();
                    e?.stopPropagation();
                    onSelect(index);
                }} style={{
                    ...BARE_BUTTON,
                    ...centeredSlot(distance, spread, scale),
                    color: isCenter ? "#fff" : "rgba(255,255,255,0.4)",
                    fontSize: isCenter ? 20 : 16,
                    fontWeight: 500,
                    opacity: Math.max(0.2, 1 - distanceAbs * 0.3),
                    transition: scrollProgress !== 0 ? "none" : "all 0.3s ease",
                }}>
              {isCenter ? tab : "●"}
            </MobileTabButton>);
        })}
        <MobileTabButton ariaLabel="Next category" disabled={atEnd} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onNext();
        }} style={{
            ...BARE_BUTTON,
            position: "absolute",
            left: "50%",
            marginLeft: 120,
            top: "50%",
            transform: "translate(-50%, -50%)",
            color: atEnd ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.4)",
            fontSize: 20,
        }}>
          ⟩
        </MobileTabButton>
      </>
    );
}
function MobileTabsHybrid6({ tabs, activeTab, onSelect, spread, scrollProgress, onPrev, onNext, atStart, atEnd, }: TabsProps): ReactElement {
    // Center is `[ Text ]`. Inactive is `+`.
    return (
      <>
        <MobileTabButton ariaLabel="Previous category" disabled={atStart} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onPrev();
        }} style={{
            ...BARE_BUTTON,
            position: "absolute",
            left: "50%",
            marginLeft: -130,
            top: "50%",
            transform: "translate(-50%, -50%)",
            color: atStart ? "rgba(95,243,184,0.2)" : MINT,
            fontSize: 16,
        }}>
          {"<-"}
        </MobileTabButton>
        {tabs.map((tab, index) => {
            const distance = index - activeTab - scrollProgress;
            const distanceAbs = Math.abs(distance);
            const isCenter = distanceAbs < 0.5;
            return (<MobileTabButton key={tab} ariaLabel={tab} onClick={(e) => {
                    e?.preventDefault();
                    e?.stopPropagation();
                    onSelect(index);
                }} style={{
                    ...BARE_BUTTON,
                    ...centeredSlot(distance, spread),
                    color: isCenter ? MINT : "rgba(255,255,255,0.3)",
                    fontSize: isCenter ? 18 : 22,
                    fontWeight: 600,
                    opacity: Math.max(0.2, 1 - distanceAbs * 0.25),
                    transition: scrollProgress !== 0 ? "none" : "all 0.3s ease",
                }}>
              {isCenter ? `[ ${tab} ]` : "+"}
            </MobileTabButton>);
        })}
        <MobileTabButton ariaLabel="Next category" disabled={atEnd} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onNext();
        }} style={{
            ...BARE_BUTTON,
            position: "absolute",
            left: "50%",
            marginLeft: 130,
            top: "50%",
            transform: "translate(-50%, -50%)",
            color: atEnd ? "rgba(95,243,184,0.2)" : MINT,
            fontSize: 16,
        }}>
          {"->"}
        </MobileTabButton>
      </>
    );
}
function MobileTabsHybrid7({ tabs, activeTab, onSelect, spread, scrollProgress, onPrev, onNext, atStart, atEnd, }: TabsProps): ReactElement {
    // Center is glowing text. Inactive are vertical bars `|`.
    return (
      <>
        <MobileTabButton ariaLabel="Previous category" disabled={atStart} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onPrev();
        }} style={{
            ...BARE_BUTTON,
            position: "absolute",
            left: "50%",
            marginLeft: -140,
            top: "50%",
            transform: "translate(-50%, -50%)",
            color: atStart ? "rgba(95,243,184,0.2)" : MINT,
            textShadow: atStart ? "none" : "0 0 10px rgba(95,243,184,0.6)",
            fontSize: 24,
            paddingBottom: 4,
        }}>
          «
        </MobileTabButton>
        {tabs.map((tab, index) => {
            const distance = index - activeTab - scrollProgress;
            const distanceAbs = Math.abs(distance);
            const isCenter = distanceAbs < 0.5;
            return (<MobileTabButton key={tab} ariaLabel={tab} onClick={(e) => {
                    e?.preventDefault();
                    e?.stopPropagation();
                    onSelect(index);
                }} style={{
                    ...BARE_BUTTON,
                    ...centeredSlot(distance, spread),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: Math.max(0.2, 1 - distanceAbs * 0.35),
                    transition: scrollProgress !== 0 ? "none" : "all 0.3s ease",
                }}>
              {isCenter ? (<span style={{
                        color: MINT,
                        fontSize: 18,
                        fontWeight: 700,
                        textShadow: "0 0 12px rgba(95,243,184,0.6)",
                    }}>
                  {tab}
                </span>) : (<div style={{
                        width: 3,
                        height: 14,
                        borderRadius: 2,
                        backgroundColor: "rgba(255,255,255,0.4)",
                    }}/>)}
            </MobileTabButton>);
        })}
        <MobileTabButton ariaLabel="Next category" disabled={atEnd} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onNext();
        }} style={{
            ...BARE_BUTTON,
            position: "absolute",
            left: "50%",
            marginLeft: 140,
            top: "50%",
            transform: "translate(-50%, -50%)",
            color: atEnd ? "rgba(95,243,184,0.2)" : MINT,
            textShadow: atEnd ? "none" : "0 0 10px rgba(95,243,184,0.6)",
            fontSize: 24,
            paddingBottom: 4,
        }}>
          »
        </MobileTabButton>
      </>
    );
}
function MobileTabsHybrid8({ tabs, activeTab, onSelect, spread, scrollProgress, onPrev, onNext, atStart, atEnd, }: TabsProps): ReactElement {
    // Center is big text. Inactive is `—` (em-dash).
    return (
      <>
        <MobileTabButton ariaLabel="Previous category" disabled={atStart} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onPrev();
        }} style={{
            ...BARE_BUTTON,
            position: "absolute",
            left: "50%",
            marginLeft: -130,
            top: "50%",
            transform: "translate(-50%, -50%)",
            color: atStart ? "rgba(255,255,255,0.2)" : "#fff",
            fontSize: 20,
        }}>
          ←
        </MobileTabButton>
        {tabs.map((tab, index) => {
            const distance = index - activeTab - scrollProgress;
            const distanceAbs = Math.abs(distance);
            const isCenter = distanceAbs < 0.5;
            return (<MobileTabButton key={tab} ariaLabel={tab} onClick={(e) => {
                    e?.preventDefault();
                    e?.stopPropagation();
                    onSelect(index);
                }} style={{
                    ...BARE_BUTTON,
                    ...centeredSlot(distance, spread),
                    color: isCenter ? "#fff" : "rgba(95,243,184,0.6)",
                    fontSize: isCenter ? 20 : 26,
                    fontWeight: isCenter ? 500 : 300,
                    opacity: Math.max(0.2, 1 - distanceAbs * 0.25),
                    transition: scrollProgress !== 0 ? "none" : "all 0.3s ease",
                }}>
              {isCenter ? tab : "-"}
            </MobileTabButton>);
        })}
        <MobileTabButton ariaLabel="Next category" disabled={atEnd} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onNext();
        }} style={{
            ...BARE_BUTTON,
            position: "absolute",
            left: "50%",
            marginLeft: 130,
            top: "50%",
            transform: "translate(-50%, -50%)",
            color: atEnd ? "rgba(255,255,255,0.2)" : "#fff",
            fontSize: 20,
        }}>
          →
        </MobileTabButton>
      </>
    );
}
function MobileTabsHybrid9({ tabs, activeTab, onSelect, spread, scrollProgress, onPrev, onNext, atStart, atEnd, }: TabsProps): ReactElement {
    // Center is a mint dot with text below it. Inactive are simple dots.
    return (
      <div style={{ position: "relative", width: "100%", height: 40 }}>
        <MobileTabButton ariaLabel="Previous category" disabled={atStart} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onPrev();
        }} style={{
            ...BARE_BUTTON,
            position: "absolute",
            left: "50%",
            marginLeft: -120,
            top: -4,
            color: atStart ? "rgba(95,243,184,0.2)" : MINT,
            fontSize: 26,
        }}>
          ‹
        </MobileTabButton>
        {tabs.map((tab, index) => {
            const distance = index - activeTab - scrollProgress;
            const distanceAbs = Math.abs(distance);
            const isCenter = distanceAbs < 0.5;
            return (<MobileTabButton key={tab} ariaLabel={tab} onClick={(e) => {
                    e?.preventDefault();
                    e?.stopPropagation();
                    onSelect(index);
                }} style={{
                    ...BARE_BUTTON,
                    ...centeredSlot(distance, spread),
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    opacity: Math.max(0.2, 1 - distanceAbs * 0.3),
                    transition: scrollProgress !== 0 ? "none" : "all 0.3s ease",
                }}>
              <div style={{
                    width: 8,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: isCenter ? MINT : "rgba(255,255,255,0.3)",
                    boxShadow: isCenter
                        ? "0 0 10px rgba(95,243,184,0.5)"
                        : "none",
                    transition: scrollProgress !== 0 ? "none" : "all 0.3s ease",
                }}/>
              {isCenter && (<span style={{
                        position: "absolute",
                        top: 14,
                        color: MINT,
                        fontSize: 16,
                        fontWeight: 600,
                    }}>
                  {tab}
                </span>)}
            </MobileTabButton>);
        })}
        <MobileTabButton ariaLabel="Next category" disabled={atEnd} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onNext();
        }} style={{
            ...BARE_BUTTON,
            position: "absolute",
            left: "50%",
            marginLeft: 120,
            top: -4,
            color: atEnd ? "rgba(95,243,184,0.2)" : MINT,
            fontSize: 26,
        }}>
          ›
        </MobileTabButton>
      </div>
    );
}
function MobileTabsHybrid10({ tabs, activeTab, onSelect, spread, scrollProgress, onPrev, onNext, atStart, atEnd, }: TabsProps): ReactElement {
    // A circular target `o`. Center is text. Inactive are `o`.
    return (
      <>
        <MobileTabButton ariaLabel="Previous category" disabled={atStart} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onPrev();
        }} style={{
            ...BARE_BUTTON,
            position: "absolute",
            left: "50%",
            marginLeft: -100,
            top: "50%",
            transform: "translate(-50%, -50%)",
            color: atStart ? "rgba(95,243,184,0.2)" : MINT,
            borderWidth: 1,
            borderStyle: "solid",
            borderColor: atStart
                ? "rgba(95,243,184,0.1)"
                : "rgba(95,243,184,0.3)",
            borderRadius: "50%",
            width: 26,
            height: 26,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            paddingBottom: 2,
        }}>
          ‹
        </MobileTabButton>
        <div style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 40,
            height: 40,
            borderRadius: 20,
            backgroundColor: "rgba(95,243,184,0.1)",
            border: "1px solid rgba(95,243,184,0.3)",
            pointerEvents: "none",
        }}/>
        {tabs.map((tab, index) => {
            const distance = index - activeTab - scrollProgress;
            const distanceAbs = Math.abs(distance);
            const isCenter = distanceAbs < 0.5;
            return (<MobileTabButton key={tab} ariaLabel={tab} onClick={(e) => {
                    e?.preventDefault();
                    e?.stopPropagation();
                    onSelect(index);
                }} style={{
                    ...BARE_BUTTON,
                    ...centeredSlot(distance, spread),
                    color: isCenter ? MINT : "rgba(255,255,255,0.4)",
                    fontSize: isCenter ? 14 : 12,
                    fontWeight: 600,
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    opacity: Math.max(0.1, 1 - distanceAbs * 0.3),
                    transition: scrollProgress !== 0 ? "none" : "all 0.3s ease",
                }}>
              {isCenter ? tab : "o"}
            </MobileTabButton>);
        })}
        <MobileTabButton ariaLabel="Next category" disabled={atEnd} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onNext();
        }} style={{
            ...BARE_BUTTON,
            position: "absolute",
            left: "50%",
            marginLeft: 100,
            top: "50%",
            transform: "translate(-50%, -50%)",
            color: atEnd ? "rgba(95,243,184,0.2)" : MINT,
            borderWidth: 1,
            borderStyle: "solid",
            borderColor: atEnd
                ? "rgba(95,243,184,0.1)"
                : "rgba(95,243,184,0.3)",
            borderRadius: "50%",
            width: 26,
            height: 26,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            paddingBottom: 2,
        }}>
          ›
        </MobileTabButton>
      </>
    );
}
const MOBILE_TAB_RENDERERS: Record<MobileTabVariantLocal, (props: TabsProps) => ReactElement> = {
    [MOBILE_TAB_VARIANT.Reel]: MobileTabsReel,
    [MOBILE_TAB_VARIANT.Reel2]: MobileTabsReel2,
    [MOBILE_TAB_VARIANT.Reel3]: MobileTabsReel3,
    [MOBILE_TAB_VARIANT.Carousel]: MobileTabsCarousel,
    [MOBILE_TAB_VARIANT.Pills]: MobileTabsPills,
    [MOBILE_TAB_VARIANT.Underline]: MobileTabsUnderline,
    [MOBILE_TAB_VARIANT.Dots]: MobileTabsDots,
    [MOBILE_TAB_VARIANT.Beads]: MobileTabsBeads,
    [MOBILE_TAB_VARIANT.Track]: MobileTabsTrack,
    [MOBILE_TAB_VARIANT.Glow]: MobileTabsGlow,
    [MOBILE_TAB_VARIANT.Hybrid1]: MobileTabsHybrid1,
    [MOBILE_TAB_VARIANT.Hybrid2]: MobileTabsHybrid2,
    [MOBILE_TAB_VARIANT.Hybrid3]: MobileTabsHybrid3,
    [MOBILE_TAB_VARIANT.Hybrid4]: MobileTabsHybrid4,
    [MOBILE_TAB_VARIANT.Hybrid5]: MobileTabsHybrid5,
    [MOBILE_TAB_VARIANT.Hybrid6]: MobileTabsHybrid6,
    [MOBILE_TAB_VARIANT.Hybrid7]: MobileTabsHybrid7,
    [MOBILE_TAB_VARIANT.Hybrid8]: MobileTabsHybrid8,
    [MOBILE_TAB_VARIANT.Hybrid9]: MobileTabsHybrid9,
    [MOBILE_TAB_VARIANT.Hybrid10]: MobileTabsHybrid10,
};
// ---------------------------------------------------------------------------
// Dispatcher — flanks the active variant with prev/next arrows and forwards a
// horizontal-wheel handler for trackpad stepping.
// ---------------------------------------------------------------------------
const RENDER_AREA_STYLE: CSSProperties = {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 56,
    right: 56,
};
function arrowStyle(side: MobileTabArrowSide, disabled: boolean, tight: boolean): CSSProperties {
    return {
        ...BARE_BUTTON,
        position: "absolute",
        top: "50%",
        ...(tight
            ? {
                left: "50%",
                marginLeft: side === MobileTabArrowSide.Left ? -90 : 90,
                transform: "translate(-50%, -50%)",
            }
            : {
                transform: "translateY(-50%)",
                ...(side === MobileTabArrowSide.Left ? { left: 8 } : { right: 8 }),
            }),
        width: tight ? 36 : 42,
        height: tight ? 36 : 42,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: tight ? 24 : 30,
        lineHeight: 1,
        paddingBottom: 4,
        color: disabled ? "rgba(255,255,255,0.2)" : MINT,
        backgroundColor: "rgba(0,0,0,0.32)",
        boxShadow: GLASS_PILL_SHADOW,
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? "default" : "pointer",
        zIndex: 5,
        flexShrink: 0,
    };
}
interface MobileTabsProps extends Omit<TabsProps, "onPrev" | "onNext" | "atStart" | "atEnd"> {
    variant: MobileTabVariantLocal;
    // Intro: 0 = below + hidden, 1 = settled.
    reveal: number;
    onWheel: (event: WheelEvent) => void;
    swipeHandlers: any;
}
export function Chatteaser3MobileTabs({ variant, tabs, activeTab, onSelect, spread, reveal, scrollProgress, onWheel, swipeHandlers, }: MobileTabsProps): ReactElement {
    const Renderer = MOBILE_TAB_RENDERERS[variant];
    const atStart = activeTab <= 0;
    const atEnd = activeTab >= tabs.length - 1;
    // Self-contained variants render their own arrows + bar (no standard flanking
    // arrows pinned to the screen edges): the hybrids, plus Reel.
    const isSelfContained = variant.startsWith(MOBILE_TAB_HYBRID_PREFIX) || variant.startsWith(MOBILE_TAB_REEL_PREFIX);
    const onPrev = () => onSelect(activeTab - 1);
    const onNext = () => onSelect(activeTab + 1);
    if (isSelfContained) {
        return (
        <div data-el="Cchatteaser3/MobileTabs/Root" style={{
                ...MOBILE_TABBAR_STYLE,
                transform: `translateY(${(1 - reveal) * 64}px)`,
                opacity: reveal,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }} onWheel={onWheel} {...swipeHandlers}>
          <Renderer tabs={tabs} activeTab={activeTab} onSelect={onSelect} spread={spread} scrollProgress={scrollProgress} onPrev={onPrev} onNext={onNext} atStart={atStart} atEnd={atEnd}/>
        </div>
      );
    }
    return (
      <div data-el="Cchatteaser3/MobileTabs/Root" style={{
            ...MOBILE_TABBAR_STYLE,
            transform: `translateY(${(1 - reveal) * 64}px)`,
            opacity: reveal,
        }} onWheel={onWheel} {...swipeHandlers}>
        <MobileTabButton ariaLabel="Previous category" disabled={atStart} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onPrev();
        }} style={arrowStyle(MobileTabArrowSide.Left, atStart, false)}>
          ‹
        </MobileTabButton>
        <div data-el="Cchatteaser3/MobileTabs/Viewport" style={RENDER_AREA_STYLE}>
          <Renderer tabs={tabs} activeTab={activeTab} onSelect={onSelect} spread={spread} scrollProgress={scrollProgress} onPrev={onPrev} onNext={onNext} atStart={atStart} atEnd={atEnd}/>
        </div>
        <MobileTabButton ariaLabel="Next category" disabled={atEnd} onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            onNext();
        }} style={arrowStyle(MobileTabArrowSide.Right, atEnd, false)}>
          ›
        </MobileTabButton>
      </div>
    );
}
